/**
 * Hook to detect lock conflicts from autosave errors and trigger the "kicked out" modal.
 * Wraps the onSaveError callback to detect ApiError with status 409.
 */

import { useCallback, useState } from "react";
import { ApiError } from "@porchpass/api-client";
import { useApplicationLock } from "./useApplicationLock";

interface LockedByUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface UseLockConflictDetectionReturn {
  showConflictModal: boolean;
  lockedByUser: LockedByUser | null;
  handleTakeover: () => Promise<void>;
  dismissConflict: () => void;
  wrappedOnSaveError: (error: Error) => Promise<void>;
}

export function useLockConflictDetection(
  appId: string | undefined,
  dealerId: string | undefined,
  onSaveError: (error: Error) => void,
): UseLockConflictDetectionReturn {
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictLockedByUser, setConflictLockedByUser] =
    useState<LockedByUser | null>(null);

  const { takeOverLock, refreshLockState } = useApplicationLock(
    appId,
    dealerId,
  );

  // Detect if error is a lock conflict (409 status)
  const isLockConflict = useCallback((error: unknown): error is ApiError => {
    return error instanceof ApiError && error.status === 409;
  }, []);

  // Handle takeover from conflict modal
  const handleTakeover = useCallback(async () => {
    if (!appId) return;

    try {
      await takeOverLock(appId);
      setShowConflictModal(false);
      setConflictLockedByUser(null);
    } catch (error) {
      console.error("[useLockConflictDetection] Takeover failed:", error);
      throw error;
    }
  }, [appId, takeOverLock]);

  // Dismiss conflict modal
  const dismissConflict = useCallback(() => {
    setShowConflictModal(false);
    setConflictLockedByUser(null);
  }, []);

  // Wrapped onSaveError that detects lock conflicts
  const wrappedOnSaveError = useCallback(
    async (error: Error) => {
      if (isLockConflict(error) && appId) {
        // This is a lock conflict - fetch current lock state
        try {
          const user = await refreshLockState();
          setConflictLockedByUser(user);
          setShowConflictModal(true);
        } catch (refreshError) {
          console.error(
            "[useLockConflictDetection] Failed to refresh lock state:",
            refreshError,
          );
          // Still show modal with null user info
          setShowConflictModal(true);
        }
      }

      // Always call the original onSaveError
      onSaveError(error);
    },
    [isLockConflict, appId, refreshLockState, onSaveError],
  );

  return {
    showConflictModal,
    lockedByUser: conflictLockedByUser,
    handleTakeover,
    dismissConflict,
    wrappedOnSaveError,
  };
}
